/** Recruitment-portal staff session — isolated from CRM (`auth_access_token`). */

export const PORTAL_AUTH_ACCESS_KEY = 'recruitment_portal_access_token'
export const PORTAL_AUTH_EXPIRES_KEY = 'recruitment_portal_expires_at'
export const PORTAL_AUTH_REFRESH_LOCK_KEY = 'recruitment_portal_refresh_lock'
export const PORTAL_AUTH_REMEMBER_KEY = 'recruitment_portal_remember'
export const PORTAL_AUTH_REMEMBER_LOGIN_KEY = 'recruitment_portal_remember_login'
export const PORTAL_AUTH_LOGOUT_PENDING_KEY = 'recruitment_portal_logout_pending'
/** Opaque QR access token from `/recruitment-portal/login/a/…` (or legacy `?access=`). */
export const PORTAL_ACCESS_QR_KEY = 'recruitment_portal_access_qr'
export const PORTAL_AUTH_CLIENT = 'recruitment_portal' as const


export const PORTAL_AUTH_SYNC_STORAGE_KEYS = [
  PORTAL_AUTH_ACCESS_KEY,
  PORTAL_AUTH_EXPIRES_KEY,
] as const

export type PortalAuthTokens = {
  access_token: string
  expires_in: number
}

function storageFor(remember: boolean): Storage {
  return remember ? localStorage : sessionStorage
}

function readFromBoth(key: string): string | null {
  return sessionStorage.getItem(key) || localStorage.getItem(key)
}

export function isPortalRememberLogin(): boolean {
  return localStorage.getItem(PORTAL_AUTH_REMEMBER_KEY) !== '0'
}

export function getPortalRememberedLogin(): string {
  if (!isPortalRememberLogin()) return ''
  return localStorage.getItem(PORTAL_AUTH_REMEMBER_LOGIN_KEY) ?? ''
}

export function getPortalAccessToken(): string | null {
  return readFromBoth(PORTAL_AUTH_ACCESS_KEY)
}

export function getPortalAccessExpiresAt(): number | null {
  const raw = readFromBoth(PORTAL_AUTH_EXPIRES_KEY)
  if (!raw) return null
  const n = Number(raw)
  return Number.isFinite(n) ? n : null
}

export const PORTAL_ACCESS_TOKEN_REFRESH_BUFFER_MS = 60_000

export function shouldRefreshPortalAccessToken(): boolean {
  const token = getPortalAccessToken()
  if (!token) return false
  const expiresAt = getPortalAccessExpiresAt()
  if (!expiresAt) return true
  return Date.now() >= expiresAt - PORTAL_ACCESS_TOKEN_REFRESH_BUFFER_MS
}

export function setPortalAuthTokens(tokens: PortalAuthTokens, remember: boolean): void {
  // Overwrite in place — avoid clear-before-write (cross-tab sync sees null → logout).
  localStorage.setItem(PORTAL_AUTH_REMEMBER_KEY, remember ? '1' : '0')
  const storage = storageFor(remember)
  const other = remember ? sessionStorage : localStorage
  other.removeItem(PORTAL_AUTH_ACCESS_KEY)
  other.removeItem(PORTAL_AUTH_EXPIRES_KEY)

  const ttlSec = Number(tokens.expires_in)
  const expiresIn = Number.isFinite(ttlSec) && ttlSec > 0 ? ttlSec : 3600
  storage.setItem(PORTAL_AUTH_ACCESS_KEY, tokens.access_token)
  storage.setItem(PORTAL_AUTH_EXPIRES_KEY, String(Date.now() + expiresIn * 1000))
}

export function setPortalRememberedLogin(login: string, remember: boolean): void {
  if (remember) {
    localStorage.setItem(PORTAL_AUTH_REMEMBER_LOGIN_KEY, login.trim())
  } else {
    localStorage.removeItem(PORTAL_AUTH_REMEMBER_LOGIN_KEY)
  }
}

export function clearPortalAuthTokens(): void {
  for (const key of [PORTAL_AUTH_ACCESS_KEY, PORTAL_AUTH_EXPIRES_KEY]) {
    localStorage.removeItem(key)
    sessionStorage.removeItem(key)
  }
}

export function getPortalAccessQrToken(): string | null {
  const raw = localStorage.getItem(PORTAL_ACCESS_QR_KEY) || sessionStorage.getItem(PORTAL_ACCESS_QR_KEY)
  const token = raw?.trim() ?? ''
  return token.length >= 20 ? token : null
}

export function setPortalAccessQrToken(token: string): void {
  const normalized = token.trim()
  if (normalized === '') {
    clearPortalAccessQrToken()
    return
  }
  localStorage.setItem(PORTAL_ACCESS_QR_KEY, normalized)
  sessionStorage.removeItem(PORTAL_ACCESS_QR_KEY)
}

export function clearPortalAccessQrToken(): void {
  localStorage.removeItem(PORTAL_ACCESS_QR_KEY)
  sessionStorage.removeItem(PORTAL_ACCESS_QR_KEY)
}

/**
 * Recover portal access QR token from the current URL.
 * Prefer path `/recruitment-portal/login/a/{token}` — Zalo in-app browser often drops `?access=`.
 */
export function parsePortalAccessQrFromLocation(
  href: string = typeof window !== 'undefined' ? window.location.href : '',
): string | null {
  if (!href) return null
  try {
    const url = new URL(href, typeof window !== 'undefined' ? window.location.origin : 'https://local.invalid')
    const pathMatch = url.pathname.match(/\/recruitment-portal\/login\/a\/([^/]+)\/?$/i)
    if (pathMatch?.[1]) {
      const fromPath = safeDecodeUriComponent(pathMatch[1]).trim()
      if (fromPath.length >= 20) return fromPath
    }
    const fromQuery = String(url.searchParams.get('access') ?? '').trim()
    if (fromQuery.length >= 20) return fromQuery

    const hash = url.hash.replace(/^#/, '')
    if (hash) {
      const hashPath = hash.match(/\/recruitment-portal\/login\/a\/([^/?#]+)/i)
      if (hashPath?.[1]) {
        const fromHashPath = safeDecodeUriComponent(hashPath[1]).trim()
        if (fromHashPath.length >= 20) return fromHashPath
      }
      const hashQuery = hash.includes('?') ? hash.slice(hash.indexOf('?') + 1) : hash
      const accessInHash = new URLSearchParams(hashQuery).get('access')
      const fromHashQuery = String(accessInHash ?? '').trim()
      if (fromHashQuery.length >= 20) return fromHashQuery
    }
  } catch {
    return null
  }

  return null
}

function safeDecodeUriComponent(value: string): string {
  try {
    return decodeURIComponent(value)
  } catch {
    return value
  }
}

export function markPortalLogoutPending(): void {
  sessionStorage.setItem(PORTAL_AUTH_LOGOUT_PENDING_KEY, '1')
}

export function clearPortalLogoutPending(): void {
  sessionStorage.removeItem(PORTAL_AUTH_LOGOUT_PENDING_KEY)
}

export function isPortalLogoutPending(): boolean {
  return sessionStorage.getItem(PORTAL_AUTH_LOGOUT_PENDING_KEY) === '1'
}
